import React, { useState, useMemo, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Users,
  MapPin,
  X,
  Edit,
  Trash2,
  Share2,
} from "lucide-react";
import Button from "../Button";
import { Badge } from "../Badge";
import Avatar from "../Avatar";
import Modal from "../Modal";

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: Date;
  end: Date;
  type: "meeting" | "task" | "reminder";
  priority: "low" | "medium" | "high";
  attendees?: string[];
  location?: string;
  color?: string;

  organizer?: { email?: string; self?: boolean };
  htmlLink?: string;
  taskPriorityName?: string;
  taskStatusName?: string;
  taskTypeName?: string;
}
export interface CalendarProps {
  events?: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onAddEvent?: (type: "task" | "meeting") => void;
  onDateSelect?: (date: Date) => void;
  className?: string;
  setCurrentDate: (date: Date) => void;
  currentDate: Date;
  viewType?: ViewType;
  onViewTypeChange?: (view: ViewType) => void;
}

type ViewType = "day" | "week" | "month" | "today";

const Calendar: React.FC<CalendarProps> = ({
  events = [],
  onEventClick,
  onAddEvent,
  onDateSelect,
  className = "",
  setCurrentDate,
  currentDate,
  viewType,
  onViewTypeChange,
}) => {
  // const [viewType, setViewType] = useState<ViewType>("week");
  const [internalViewType, setInternalViewType] = useState<ViewType>("week");

  const activeViewType = viewType ?? internalViewType;

  const setActiveViewType = (view: ViewType) => {
    if (!viewType) {
      setInternalViewType(view);
    }
    onViewTypeChange?.(view);
  };

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );
  const [showEventModal, setShowEventModal] = useState<boolean>(false);

  const [dayModalEvents, setDayModalEvents] = useState<CalendarEvent[] | null>(
    null,
  );
  const [dayModalDate, setDayModalDate] = useState<Date | null>(null);

  useEffect(() => {}, [showEventModal]);

  const allEvents = [...events];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay, year, month };
  };

  const getWeekDays = (date: Date) => {
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      weekDays.push(day);
    }
    return weekDays;
  };
  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);
  const weekStart = weekDays[0];
  const weekEnd = weekDays[weekDays.length - 1];
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

  const getDayEvents = (date: Date) =>
    allEvents.filter((event) => sameDay(new Date(event.start), date));

  const getWeekEvents = (weekDays: Date[]) => {
    const weekEvents: { [key: string]: CalendarEvent[] } = {};
    weekDays.forEach((day) => {
      weekEvents[day.toDateString()] = getDayEvents(day);
    });
    return weekEvents;
  };

  const getMonthEvents = () => {
    const { daysInMonth, year, month } = getDaysInMonth(currentDate);
    const monthEvents: { [key: string]: CalendarEvent[] } = {};
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      monthEvents[date.toDateString()] = getDayEvents(date);
    }
    return monthEvents;
  };

  const getTodayEvents = () => getDayEvents(new Date());

  const navigateDate = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    switch (activeViewType) {
      case "day":
        newDate.setDate(
          currentDate.getDate() + (direction === "next" ? 1 : -1),
        );
        break;
      case "week":
        newDate.setDate(
          currentDate.getDate() + (direction === "next" ? 7 : -7),
        );
        break;
      case "month":
        newDate.setMonth(
          currentDate.getMonth() + (direction === "next" ? 1 : -1),
        );
        break;
      case "today":
        return;
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDayModalEvents(null);
    setDayModalDate(null);
    setShowEventModal(true);
    onEventClick?.(event);
  };

  const openEventModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDayModalEvents(null);
    setDayModalDate(null);
    setShowEventModal(true);
  };

  const openDayModal = (date: Date, eventsForDay: CalendarEvent[]) => {
    setSelectedEvent(null);
    setDayModalEvents(
      [...eventsForDay].sort((a, b) => a.start.getTime() - b.start.getTime()),
    );
    setDayModalDate(date);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setDayModalEvents(null);
    setDayModalDate(null);
  };

  const handleEditEvent = () => {
    closeEventModal();
  };

  const handleDeleteEvent = () => {
    closeEventModal();
  };

  const handleShareEvent = () => {};

  const formatTime = (date: Date) =>
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "medium":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "low":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getEventColor = (event: CalendarEvent) => {
    if (event.color) {
      const colorMap: { [key: string]: string } = {
        blue: "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700",
        green:
          "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700",
        orange:
          "bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-700",
        purple:
          "bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-700",
        red: "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700",
      };
      return (
        colorMap[event.color] ||
        "bg-gray-50 border-gray-200 dark:bg-gray-900/20 dark:border-gray-700"
      );
    }
    return "bg-gray-50 border-gray-200 dark:bg-gray-900/20 dark:border-gray-700";
  };

  const renderDayView = () => {
    const dayEvents = getDayEvents(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    return (
      <div className="h-[600px] overflow-y-auto">
        <div className="grid grid-cols-1 gap-1">
          {hours.map((hour) => {
            const hourEvents = dayEvents.filter(
              (event) => event.start.getHours() === hour,
            );
            return (
              <div
                key={hour}
                className="flex border-b border-gray-200 dark:border-gray-700"
              >
                <div className="w-20 p-2 text-sm text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">
                  {formatTime(
                    new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth(),
                      currentDate.getDate(),
                      hour,
                    ),
                  )}
                </div>
                <div className="flex-1 p-2 min-h-[60px] relative">
                  {hourEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`absolute left-2 right-2 p-2 rounded border ${getEventColor(event)} cursor-pointer hover:shadow-md transition-shadow`}
                      style={{
                        top: `${(event.start.getMinutes() / 60) * 60}px`,
                        height: `${Math.max(
                          30,
                          ((event.end.getTime() - event.start.getTime()) /
                            (1000 * 60 * 60)) *
                            60,
                        )}px`,
                      }}
                      onClick={() => openEventModal(event)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {event.title}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {formatTime(event.start)} - {formatTime(event.end)}
                          </div>
                        </div>
                        <Badge
                          className={getPriorityColor(event.priority)}
                          size="sm"
                        >
                          {event.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(currentDate);
    const weekEvents = getWeekEvents(weekDays);
    return (
      <div className="h-[600px] overflow-y-auto">
        <div className="grid grid-cols-8 gap-1">
          <div className="border-r border-gray-200 dark:border-gray-700">
            <div className="h-12"></div>
            {Array.from({ length: 24 }, (_, i) => (
              <div
                key={i}
                className="h-12 border-b border-gray-200 dark:border-gray-700 p-1 text-xs text-gray-500 dark:text-gray-400"
              >
                {formatTime(new Date(2024, 0, 1, i))}
              </div>
            ))}
          </div>

          {weekDays.map((day) => {
            const dayEvents = weekEvents[day.toDateString()] || [];
            const isToday = sameDay(day, new Date());
            return (
              <div key={day.toDateString()} className="relative">
                <div
                  className={`h-12 border-b border-gray-200 dark:border-gray-700 p-2 text-center ${
                    isToday ? "bg-blue-50 dark:bg-blue-900/20" : ""
                  }`}
                >
                  <div className="text-sm font-medium">
                    {day.toLocaleDateString("en-US", { weekday: "short" })}
                  </div>
                  <div
                    className={`text-xs ${isToday ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"}`}
                  >
                    {day.getDate()}
                  </div>
                </div>

                <div className="relative">
                  {Array.from({ length: 24 }, (_, hour) => (
                    <div
                      key={hour}
                      className="h-12  border-b border-gray-200 dark:border-gray-700 relative"
                    >
                      {dayEvents
                        .filter((event) => event.start.getHours() === hour)
                        .map((event) => (
                          <div
                            key={event.id}
                            className={`absolute left-1 right-1 p-2 overflow-hidden bg-black rounded-lg shadow-sm border cursor-pointer hover:shadow-md transition-all duration-200 ${getEventColor(event)}`}
                            style={{
                              top: `${(event.start.getMinutes() / 60) * 48}px`,
                              height: `${Math.max(
                                24,
                                ((event.end.getTime() - event.start.getTime()) /
                                  (1000 * 60 * 60)) *
                                  48,
                              )}px`,
                            }}
                            onClick={() => openEventModal(event)}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">
                                  {event.title}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                  {formatTime(event.start)} -{" "}
                                  {formatTime(event.end)}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                <div
                                  className={`w-2 h-2 rounded-full ${
                                    event.priority === "high"
                                      ? "bg-red-500"
                                      : event.priority === "medium"
                                        ? "bg-yellow-500"
                                        : "bg-green-500"
                                  }`}
                                ></div>
                                {event.attendees &&
                                  event.attendees.length > 0 && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {event.attendees.length}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {event.location && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                📍 {event.location}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const CountChip: React.FC<{ count: number; onClick: () => void }> = ({
    count,
    onClick,
  }) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="absolute top-2 right-2 text-[11px] leading-none px-2 py-1 rounded-full bg-green-500 text-white shadow hover:bg-green-600 transition-colors"
      title={`${count} events`}
    >
      {count}
    </button>
  );

  const renderMonthView = () => {
    const { daysInMonth, startingDay, year, month } =
      getDaysInMonth(currentDate);
    const monthEvents = getMonthEvents();
    const today = new Date();

    const days: { date: Date; isCurrentMonth: boolean; isToday: boolean }[] =
      [];
    const prevMonthDays = new Date(year, month, 0).getDate();

    // prev pad
    for (let i = startingDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      days.push({
        date: new Date(year, month - 1, day),
        isCurrentMonth: false,
        isToday: false,
      });
    }
    // current
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ date, isCurrentMonth: true, isToday: sameDay(date, today) });
    }
    // next pad to 6 weeks grid
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({
        date: new Date(year, month + 1, d),
        isCurrentMonth: false,
        isToday: false,
      });
    }

    return (
      <div className="h-[700px] overflow-y-auto border rounded-lg mt-2">
        <div className="grid grid-cols-7">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div
              key={d}
              className="h-12 flex items-center justify-center text-sm font-medium text-gray-500 dark:text-gray-400"
            >
              {d}
            </div>
          ))}

          {days.map(({ date, isCurrentMonth, isToday }, idx) => {
            const isSelected = sameDay(date, currentDate);
            const dayEvents = monthEvents[date.toDateString()] || [];
            const moreCount = Math.max(0, dayEvents.length - 3);

            return (
              <div
                key={idx}
                className={`relative min-h-[110px] p-2 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors
                  ${isToday ? "bg-blue-50 dark:bg-blue-900/20" : ""} 
                  ${!isCurrentMonth ? "bg-gray-50 dark:bg-gray-900/20" : ""}`}
                onClick={() => {
                  setCurrentDate(date);
                  onDateSelect?.(date);
                }}
              >
                {/* DATE BADGE */}
                <div className="mb-1">
                  <span
                    className={[
                      "inline-grid place-items-center",
                      "w-7 h-7 text-sm font-medium rounded-full",
                      isCurrentMonth
                        ? "text-gray-900 dark:text-gray-100"
                        : "text-gray-400 dark:text-gray-500",
                      isToday && !isSelected
                        ? "ring-1 ring-blue-300 dark:ring-blue-700"
                        : "",
                      isSelected
                        ? "bg-amber-500 text-white ring-2 ring-amber-200"
                        : "",
                    ].join(" ")}
                  >
                    {date.getDate()}
                  </span>
                </div>

                {/* COUNT CHIP WHEN MULTIPLE EVENTS */}
                {dayEvents.length > 1 && (
                  <CountChip
                    count={dayEvents.length}
                    onClick={() => openDayModal(date, dayEvents)}
                  />
                )}

                {/* EVENT PREVIEWS (first 3) */}
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      className={`p-1 rounded text-xs border ${getEventColor(event)} cursor-pointer hover:shadow-sm transition-shadow`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEventModal(event);
                      }}
                      title={`${event.title} • ${formatTime(event.start)}`}
                    >
                      <div className="font-medium truncate">{event.title}</div>
                      <div className="text-gray-600 dark:text-gray-400 truncate">
                        {formatTime(event.start)}
                      </div>
                    </div>
                  ))}

                  {moreCount > 0 && (
                    <button
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDayModal(date, dayEvents);
                      }}
                    >
                      +{moreCount} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTodayView = () => {
    const todayEvents = getTodayEvents();
    const today = new Date();
    return (
      <div className="h-[600px] overflow-y-auto">
        <div className="space-y-4">
          <div className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Today -{" "}
            {today.toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>

          {todayEvents.length === 0 ? (
            <div className="">
              <div className="text-center py-8 mt-40 text-gray-500 dark:text-gray-400">
                <CalendarIcon className="mx-auto h-12 w-12 mb-4" />
                <p>No events scheduled for today</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={onAddEvent}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {todayEvents
                .sort((a, b) => a.start.getTime() - b.start.getTime())
                .map((event) => (
                  <div
                    key={event.id}
                    className={`p-4 rounded-lg border ${getEventColor(event)} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => openEventModal(event)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="font-medium">{event.title}</div>
                          <Badge
                            className={getPriorityColor(event.priority)}
                            size="sm"
                          >
                            {event.priority}
                          </Badge>
                          <Badge
                            variant={
                              event.type === "meeting" ? "info" : "secondary"
                            }
                            size="sm"
                          >
                            {event.type}
                          </Badge>
                        </div>

                        {event.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {event.description}
                          </p>
                        )}

                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {formatTime(event.start)} - {formatTime(event.end)}
                          </div>

                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {event.location}
                            </div>
                          )}

                          {event.attendees && event.attendees.length > 0 && (
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {event.attendees.length} attendee
                              {event.attendees.length > 1 ? "s" : ""}
                            </div>
                          )}
                        </div>
                      </div>

                      {event.attendees && event.attendees.length > 0 && (
                        <div className="flex -space-x-2">
                          {event.attendees
                            .slice(0, 3)
                            .map((attendee, index) => (
                              <Avatar
                                key={index}
                                size="sm"
                                src={`https://ui-avatars.com/api/?name=${attendee}&background=random`}
                                alt={attendee}
                              />
                            ))}
                          {event.attendees.length > 3 && (
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-xs">
                              +{event.attendees.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderViewContent = () => {
    switch (activeViewType) {
      case "day":
        return renderDayView();
      case "week":
        return renderWeekView();
      case "month":
        return renderMonthView();
      case "today":
        return renderTodayView();
      default:
        return renderWeekView();
    }
  };

  return (
    <div className={`dark:bg-gray-900 rounded-lg shadow-sm ${className}`}>
      {/* Header */}
      <div className="">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {activeViewType === "month" &&
                  currentDate.toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}

                {activeViewType === "week" &&
                  `${weekStart?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })} - ${weekEnd?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}`}

                {activeViewType === "day" &&
                  currentDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
              </div>
            </div>

            {activeViewType !== "today" && (
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="md"
                  className="text-primary-button"
                  onClick={() => navigateDate("prev")}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  className="text-primary-button"
                  onClick={() => navigateDate("next")}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex border border-gray-200 dark:border-gray-700 rounded-lg py-1 px-2 overflow-hidden">
              {(["today", "day", "week", "month"] as ViewType[]).map((view) => (
                <button
                  key={view}
                  className={`px-3 py-2 text-sm font-medium transition-colors rounded-lg ${
                    activeViewType === view
                      ? "bg-yellow-500 text-white rounded-lg"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                  onClick={() => setActiveViewType(view)}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>{renderViewContent()}</div>

      <Modal
        open={showEventModal}
        onClose={closeEventModal}
        size="md"
        title=""
        showCloseButton={false}
      >
        {/* DAY LIST MODE */}
        {dayModalEvents && dayModalDate && (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {dayModalDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </h2>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {dayModalEvents.length} event
                  {dayModalEvents.length > 1 ? "s" : ""}
                </div>
              </div>
              <button
                onClick={closeEventModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2">
              {dayModalEvents.map((ev) => (
                <button
                  key={ev.id}
                  onClick={() => {
                    setSelectedEvent(ev);
                    setDayModalEvents(null);
                    setDayModalDate(null);
                  }}
                  className={`w-full text-left p-3 rounded-lg border hover:shadow-sm transition ${getEventColor(ev)}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium truncate">{ev.title}</div>
                    <a
                      href={ev.htmlLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Open in Google Calendar
                    </a>
                  </div>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-3">
                    <span>
                      {formatTime(ev.start)} – {formatTime(ev.end)}
                    </span>
                    {ev.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {ev.location}
                      </span>
                    )}
                    {!!ev.attendees?.length && (
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {ev.attendees!.length}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SINGLE EVENT MODE */}
        {!dayModalEvents && selectedEvent && (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {selectedEvent.title}
                </h2>
              </div>
              <button
                onClick={closeEventModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedEvent.description && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Description
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedEvent.description}
                </p>
              </div>
            )}
            {/* Task Meta Info */}
            {selectedEvent.type === "task" && (
              <div className="space-y-3">
                {selectedEvent.taskPriorityName && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Priority
                    </span>
                    <Badge variant="secondary">
                      {selectedEvent.taskPriorityName}
                    </Badge>
                  </div>
                )}

                {selectedEvent.taskStatusName && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    <Badge variant="default">
                      {selectedEvent.taskStatusName}
                    </Badge>
                  </div>
                )}

                {selectedEvent.taskTypeName && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Type
                    </span>
                    <Badge variant="info">{selectedEvent.taskTypeName}</Badge>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatTime(selectedEvent.start)} -{" "}
                    {formatTime(selectedEvent.end)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedEvent.start.toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                </div>
              </div>

              {selectedEvent.location && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Location
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedEvent.location}
                    </div>
                  </div>
                </div>
              )}

              {!!selectedEvent.attendees?.length && (
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Attendees ({selectedEvent.attendees!.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedEvent.attendees!.map((attendee, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg"
                        >
                          <Avatar
                            size="sm"
                            src={`https://ui-avatars.com/api/?name=${attendee}&background=random`}
                            alt={attendee}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {attendee}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Organizer */}
              {selectedEvent.organizer && (
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Organizer
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedEvent.organizer?.email}
                    </div>
                  </div>
                </div>
              )}

              {/* Google Calendar deep link, if present */}
              {selectedEvent.htmlLink && (
                <div className="mt-2">
                  <a
                    href={selectedEvent.htmlLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Open in Google Calendar
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Calendar;
